/**
 * Persist the active guest quote across Clerk **register** (the only
 * live-broken path: new account / first OTP). Existing-account sign-in
 * already keeps the quote (`fallbackRedirectUrl` → `/order/:id`); do not
 * force-override that working path.
 *
 * `/quote` keeps `quoteId` in React state only. Clerk email OTP on
 * account creation remounts the SPA and historically dropped that id, so
 * the customer landed on an empty quote page. sessionStorage + localStorage
 * survive the same-tab OTP round-trip; localStorage also covers a magic
 * link that opens a new tab on the same origin.
 *
 * Quote ids are unguessable UUIDs in production. Test / e2e ids are
 * path-safe slugs (`q-123`). Never store another customer's id — the
 * server claim rule is the real ownership gate.
 *
 * Home / landing Sign in must not resume a leftover localStorage id
 * (that sent empty accounts to Place order). Use sessionStorage for
 * "this-tab open quote"; clear both stores on sign-out.
 */

const QUOTE_KEY = "fprint:resume-quote-id";
const AFTER_AUTH_KEY = "fprint:resume-after-auth";

/** Production UUIDs plus the short slugs used by Playwright mocks. */
const QUOTE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9-]{0,79}$/;

function safeStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function durableStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function isResumeQuoteId(value: string | null | undefined): value is string {
  return Boolean(value && QUOTE_ID_RE.test(value));
}

export function persistResumeQuoteId(id: string): void {
  if (!isResumeQuoteId(id)) return;
  safeStore()?.setItem(QUOTE_KEY, id);
  durableStore()?.setItem(QUOTE_KEY, id);
}

export function readResumeQuoteId(): string | null {
  const raw = safeStore()?.getItem(QUOTE_KEY) ?? durableStore()?.getItem(QUOTE_KEY);
  return isResumeQuoteId(raw) ? raw : null;
}

/** This-tab open quote only. Ignores leftover localStorage from another visit. */
export function readSessionResumeQuoteId(): string | null {
  const raw = safeStore()?.getItem(QUOTE_KEY);
  return isResumeQuoteId(raw) ? raw : null;
}

/** Home / landing Sign in: this-tab quote, else My orders. Never a leftover. */
export function homeSignInFrom(): string {
  const sessionId = readSessionResumeQuoteId();
  return sessionId ? `/order/${sessionId}` : "/orders";
}

/** Persist + mark only for an explicit `/order/:id` target — never leftover storage. */
export function markQuoteResumeFromSignInTarget(target: string): void {
  const quoteId = quoteIdFromOrderPath(target);
  if (!quoteId) {
    // Home / landing: drop a leftover durable id so My orders does not
    // claim a stale guest quote after this sign-in.
    durableStore()?.removeItem(QUOTE_KEY);
    return;
  }
  persistResumeQuoteId(quoteId);
  markResumeAfterAuth();
}

export function clearResumeQuoteId(): void {
  safeStore()?.removeItem(QUOTE_KEY);
  durableStore()?.removeItem(QUOTE_KEY);
  safeStore()?.removeItem(AFTER_AUTH_KEY);
}

/** Set when the customer starts the Clerk round-trip so a dump onto `/` still resumes. */
export function markResumeAfterAuth(): void {
  safeStore()?.setItem(AFTER_AUTH_KEY, "1");
}

/** Peek without consuming — used when `from` is missing mid-OTP. */
export function peekResumeAfterAuth(): boolean {
  return safeStore()?.getItem(AFTER_AUTH_KEY) === "1";
}

/** One-shot: true if this tab just finished sign-in / register. */
export function consumeResumeAfterAuth(): boolean {
  const store = safeStore();
  if (store?.getItem(AFTER_AUTH_KEY) !== "1") return false;
  store.removeItem(AFTER_AUTH_KEY);
  return true;
}

/**
 * Register safety net: only after a quote-intent mark. Prefer this-tab
 * sessionStorage; localStorage-only leftovers are used only when this tab
 * marked resume (same-tab OTP whose session store was cleared).
 */
export function takeResumeQuoteIdAfterAuth(): string | null {
  if (!consumeResumeAfterAuth()) return null;
  return readSessionResumeQuoteId() ?? readResumeQuoteId();
}

export function orderResumePath(quoteId: string): string {
  return `/order/${quoteId}?autoplace=1`;
}

function appendAutoplace(target: string): string {
  if (!target.startsWith("/order/")) return target;
  if (target.includes("autoplace=")) return target;
  return `${target}${target.includes("?") ? "&" : "?"}autoplace=1`;
}

/**
 * Where Clerk should send the customer after sign-in or sign-up.
 *
 * Prefer an explicit `from` (router state or `?from=`). `from=/` and
 * `from=/orders` stay as given — leftover localStorage must not rewrite
 * a Home / landing click. When `from` is missing, resume only a this-tab
 * session quote, or a stored id if this tab marked quote-intent (register
 * / OTP refresh). Otherwise land on My orders.
 */
export function resolveSignInRedirect(from?: string | null): string {
  const explicit = from && from.startsWith("/") ? from : null;
  if (explicit) return appendAutoplace(explicit);

  const sessionId = readSessionResumeQuoteId();
  if (sessionId) return appendAutoplace(`/order/${sessionId}`);

  if (peekResumeAfterAuth()) {
    const stored = readResumeQuoteId();
    if (stored) return appendAutoplace(`/order/${stored}`);
  }

  return "/orders";
}

export function clerkRedirectProps(redirectUrl: string): {
  fallbackRedirectUrl: string;
  signUpFallbackRedirectUrl: string;
  forceRedirectUrl?: string;
  signUpForceRedirectUrl?: string;
} {
  const base = {
    fallbackRedirectUrl: redirectUrl,
    signUpFallbackRedirectUrl: redirectUrl,
  };
  // Register / first OTP is the only broken path: Clerk dashboard
  // after-sign-up (often `/`) wins over fallback*. Force sign-up only.
  // Existing sign-in already works via fallbackRedirectUrl — do not set
  // forceRedirectUrl (that would override a working dashboard/sign-in).
  if (redirectUrl.startsWith("/order/")) {
    return {
      ...base,
      signUpForceRedirectUrl: redirectUrl,
    };
  }
  return base;
}

export function quoteIdFromOrderPath(pathname: string): string | null {
  const match = pathname.match(/^\/order\/([^/?#]+)/);
  const id = match?.[1] ?? null;
  return isResumeQuoteId(id) ? id : null;
}
